import moment from 'moment-timezone';

import {TZ} from 'modules/Common/consts';

export function timeStampToUnix(timeStamp) {
  const momentTs = moment(timeStamp);
  if (!momentTs.isValid()) {
    return 0;
  }
  return momentTs.unix();
}

export function sameDate(d1, d2) {
  return moment(d1).isValid() && moment(d2).isValid()
    && isoDate(moment(d1)) === isoDate(moment(d2));
}

export function momentInUTC(d) {
  return moment.utc(d).tz(TZ);
}

function numberOfHoursSinceNow(d){
  return moment.utc().startOf('day').diff(moment.utc(d).startOf('day'), 'hours');
}

export function isToday(d) {
  return numberOfHoursSinceNow(d) === 0;
}

export function isYesterday(d) {
  return numberOfHoursSinceNow(d) === 24;
}

export function isTomorrow(d) {
  return numberOfHoursSinceNow(d) === -24;
}

export function isInThePast(d){
  return moment().valueOf() > momentInUTC(d).valueOf();
}

export function addDuration(d, ...t){
  return momentInUTC(d).add(...t);
}

// Spread param to support moment's options for `add`;
export function isBefore(d, ...t){
  return momentInUTC().isBefore(momentInUTC(d).add(...t));
}

export function local12Hour(time) {

  const momentTime = moment(time, 'HH:mm');

  if (!momentTime.isValid()) {
    return "";
  }
  
  return momentTime.format('h a');
}

export function localDateTime(d){
  return momentInUTC(d).format("DD-MM-YYYY HH:mm");
}

export function localDateTimeLong(d){
  return momentInUTC(d).format("DD-MM-YYYY HH:mm:ss");
}

export function isoDate(d){
  return momentInUTC(d).format("YYYY-MM-DD");
}

export function localDate(d){
  return momentInUTC(d).format("DD-MM-YYYY");
}

export function localDateShort(d, {convertToday, convertYesterday, convertTomorrow} = {}) {
    if(convertToday && isToday(d)){
      return 'Today';
    }
    else if(convertYesterday && isYesterday(d)) {
      return 'Yesterday';
    }
    else if(convertTomorrow && isTomorrow(d)) {
      return 'Tomorrow';
    }
    else {
      return momentInUTC(d).format('ddd DD');
    }
}

export function getRelativeDay(d, difference, {convertToday, convertYesterday, convertTomorrow} = {}) {
    d = moment.utc(d).add(difference, 'days').tz(TZ);
    if(convertToday && isToday(d)){
      return '';
    }
    else if(convertYesterday && isYesterday(d)) {
      return 'yesterday';
    }
    else if(convertTomorrow && isTomorrow(d)) {
      return 'tomorrow';
    }
    else {
      return `${isoDate(d)}`;
    }
}

// For URIs
export function getRelativeDayURI(d, difference, {convertToday, convertYesterday, convertTomorrow} = {}) {
    let uri = getRelativeDay(d, difference, {convertToday, convertYesterday, convertTomorrow});
    if(uri != '')
    {
      uri = `/${uri}`;
    }
    return uri;
}

export function localDateShortDescriptive(d){
  return momentInUTC(d).format('ddd Do');
}

export function localFullDate(d){
  return momentInUTC(d).format("Do MMMM YYYY");
}

export function localLongDate(d) {
  return momentInUTC(d).format("dddd MMMM D")
}

export function localFullDateTime(d){
  return momentInUTC(d).format("HH:mm Do MMMM YYYY");
}

export function localFullDateLong(d){
  return momentInUTC(d).format("ddd D MMMM YYYY");
}

export function localFullDateShort(d){
  return momentInUTC(d).format("DD/MM/YY");
}

export function localFullDateNoDay(d){
  return momentInUTC(d).format("D MMMM YYYY");
}

export function localMonth(d) {
  return momentInUTC(d).format("MMM");
}

export function localLongMonth(d) {
  return momentInUTC(d).format("MMMM");
}

export function localYearMonth(d) {
  return momentInUTC(d).format("YYYY-MM");
}

export function localDayOfWeek(d) {
  return moment(d).format("dddd");
}

export function localShortDayOfWeek(d) {
  return moment(d).format("ddd");
}

export function yearMonthToMoment(d) {
  return moment(d, "YYYY-MM");
}

export function localTime(t){
  return momentInUTC(t).format("HH:mm:ss");
}

export function localDateTimeShort(t){
  return momentInUTC(t).format("HH:mm DD/MM/YY");
}

export function localTimeShort(t){
  return momentInUTC(t).format("HH:mm");
}

export function getTodaysDate(format) {
  return moment().tz(TZ).format(format);
}

export function getYesterdaysDate(format) {
  return moment().add(-1, 'days').tz(TZ).format(format);
}

export function getTomorrowsDate(format) {
  return moment().add(1, 'days').tz(TZ).format(format);
}

export function beforeNow(timestamp) {
  // duration units as ms
  const min   = 1000 * 60;
  const hour  = min * 60;
  const day   = hour * 24;
  const week  = day * 7;
  // year and month are approximate, but good enough in this scenario
  const year  = day * 365.25;
  const month = year / 12;

  // boundaries and units
  const gaps = [
    [ hour,  45 * min , "h"],
    [  day,  20 * hour, "d"],
    [ week,   6 * day , "w"],
    [month,  26 * day , "M"],
    [ year, 350 * day , "y"]
  ];

  let gapMs = Math.abs(moment.now() - momentInUTC(timestamp).valueOf());

  // start by assuming minutes
  let gapCalc = gapMs / min;
  let gapUnit = "m";

  // then move up the scale
  gaps.forEach(([scale, boundary, unit]) => {
    if (gapMs > boundary) {
      gapCalc = gapMs / scale;
      gapUnit = unit;
    }
  });

  // format the result
  const gapString = `${Math.round(gapCalc)}${gapUnit}`;

  // but prevent zero
  if (gapString == "0m") {
    return "1m";
  }
  else {
    return gapString;
  }
}



// WEBPACK FOOTER //
// ./utils/LocalDateTimeHelper.js